import { PropsWithChildren, useContext, useEffect, useMemo } from 'react'

import isPlainObject from 'lodash.isplainobject'
import { createPortal } from 'react-dom'
import { FrameContext } from 'react-frame-component'

import { GlobalContext } from 'hsds/components/hsds'
import { isNodeElement } from 'hsds/utils/dom'

import Container, { ID as portalContainerId } from './Portal.Container'

export type PortalProps = {
  /** Custom class names to be added to the component. */
  className?: string
  /** The ID for the component. */
  id?: string
  /** A CSS selector to render content, instead of the `<body>`. */
  renderTo?: string | Node
  /** Fires as soon as the component has rendered. */
  onOpen?: () => void
  /** Fires after the component is unmounted. */
  onClose?: () => void
  /** Delay before the Portal'ed component is unmounted from the DOM. Default is `0`. */
  timeout?: number
}

const getMountSelector = (
  renderTo: PortalProps['renderTo'],
  body: unknown
): HTMLElement | null => {
  let mountSelector
  // 1. Prioritize renderTo selector
  if (renderTo) {
    mountSelector =
      typeof renderTo === 'string' ? document.querySelector(renderTo) : false
    mountSelector =
      isPlainObject(renderTo) && isNodeElement(renderTo)
        ? renderTo
        : mountSelector
  }

  // 2- is inside an iframe, but not the one created by storybook/cypress
  if (
    !mountSelector &&
    !body &&
    window.parent &&
    // @ts-ignore - custom property
    !window.STORYBOOK_ENV &&
    // @ts-ignore - custom property
    !window.Cypress
  ) {
    mountSelector = window.parent.document.body
  }

  // 3. Fallback to <Portal.Container />
  mountSelector = mountSelector || document.getElementById(portalContainerId)

  // 4. Fallback to document.body
  const doc = body || window.document

  return (mountSelector ||
    (doc as { body: Element | null }).body) as HTMLElement | null // fallback
}

const Portal = ({
  children,
  renderTo,
  timeout,
  onClose,
  onOpen,
  className,
  id,
}: PropsWithChildren<PortalProps>) => {
  const { getCurrentScope } = useContext(GlobalContext) || {}
  const scope = getCurrentScope ? getCurrentScope() : null

  const frameContext = useContext(FrameContext)
  const body = frameContext ? frameContext.document : null

  const el = useMemo(() => {
    const div = document.createElement('div')
    if (scope) div.classList.add(scope)
    if (className) div.classList.add(...className.split(' '))
    if (id) div.id = id

    return div
  }, [scope, id, className])

  const mount = useMemo(
    () => getMountSelector(renderTo, body),
    [renderTo, body]
  )

  useEffect(() => {
    mount?.appendChild(el)
    if (onOpen) onOpen()

    return () => {
      const unmount = () => {
        if (onClose) onClose()
        if (mount?.contains(el)) mount.removeChild(el)
      }

      if (timeout === 0) {
        unmount()
      } else {
        setTimeout(unmount, timeout)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return createPortal(children, el)
}

Portal.Container = Container

export default Portal
